import { DeleteOutlined } from "@ant-design/icons";
import { Button, Col, Input, InputNumber, Row, Select, TimePicker, Typography } from "antd";
import { TagTextInput } from "components/CC/UI/TagInput/TagTextInput";
import { useFilterEditorContext } from "components/UI/BaseFilter/FilterEditorContext.tsx";
import {
    FilterAffiliateRule,
    FilterCapRule,
    FilterFieldRule,
    FilterRule,
    FilterTimeRule,
    FilterWeekdayRule,
} from "components/UI/BaseFilter/types/filterEditor";
import {
    buildCapTag,
    CAP_KIND_ORDER,
    getCapPeriodOptions,
    hasCapKind,
    parseCapTag,
} from "components/UI/BaseFilter/utils/capRuleFields";
import {
    FILTER_COLUMNS,
    getCapTypesForContext,
    getRuleColumn,
} from "components/UI/BaseFilter/utils/filterEditorFactory";
import { FIELD_OPERATORS } from "components/UI/BaseFilter/utils/filterFieldOperators";
import {
    CAP_OPERATORS,
    RANGE_OPERATORS,
    VALUE_LESS_OPERATORS,
    TIME_OPERATORS,
    WEEKDAYS,
} from "components/UI/BaseFilter/utils/filterTreeMapper";
import { PartnerSelectSearchable } from "components/UI/Selects/PartnerSelectSearchable/PartnerSelectSearchable.tsx";
import dayjs, { Dayjs } from "dayjs";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { ReactNode, useMemo } from "react";
import { FilterType as FilterOperator } from "types/enums";
import { handleShowSearchFilterOption } from "utils/handleShowSearchFilterOption";

interface BaseFilterItemProps {
    rule: FilterRule;
}

interface RowColumn {
    span: number;
    node: ReactNode;
}

const TIME_FORMAT = "HH:mm";

const WEEKDAY_KEYS: Record<number, string> = {
    1: "monday",
    2: "tuesday",
    3: "wednesday",
    4: "thursday",
    5: "friday",
    6: "saturday",
    7: "sunday",
};

/** Built without a format string, so it doesn't depend on dayjs' customParseFormat plugin being loaded. */
const toTimePickerValue = (value: string): Dayjs | null => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value);
    return match ? dayjs().hour(Number(match[1])).minute(Number(match[2])).second(0) : null;
};

/** Every select in the row is searchable (matches the label as well as the value). */
const searchable = { showSearch: true, filterOption: handleShowSearchFilterOption };

const stripBraces = (value: string): string => value.replace(/^\{/, "").replace(/\}$/, "");

export const BaseFilterItem = ({ rule }: BaseFilterItemProps) => {
    const { t } = usePaldockTranslation("ui.offer-filter");
    const { dispatch, context, fieldsCategory, showErrors, errorsByNode } = useFilterEditorContext();

    const errors = showErrors ? (errorsByNode.get(rule.id) ?? []) : [];
    const hasError = (field: string) => errors.some((error) => error.field === field);
    const status = (field: string) => (hasError(field) ? "error" : undefined);
    const update = (patch: object) => dispatch({ type: "updateRule", id: rule.id, patch });

    const pickerCategories = useMemo(() => (fieldsCategory ? [fieldsCategory] : []), [fieldsCategory]);

    const fieldRule = rule.kind === "field" ? (rule as FilterFieldRule) : null;
    const capRule = rule.kind === "cap" ? (rule as FilterCapRule) : null;
    const affiliateRule = rule.kind === "affiliate" ? (rule as FilterAffiliateRule) : null;
    const timeRule = rule.kind === "time" ? (rule as FilterTimeRule) : null;
    const weekdayRule = rule.kind === "weekday" ? (rule as FilterWeekdayRule) : null;

    const capTags = useMemo(() => getCapTypesForContext(context), [context]);
    const capShowsKind = hasCapKind(capTags);

    const operatorOptions = (operators: readonly FilterOperator[], current: FilterOperator) =>
        (operators.includes(current) ? operators : [current, ...operators]).map((operator) => ({
            label: t(`operators.${operator}`),
            value: operator,
        }));

    const handleTagChange = (rawValue: string) => {
        if (!fieldRule) {
            return;
        }
        update({ tag: stripBraces(rawValue) });
    };

    const ruleColumnSelect = (
        <Select
            {...searchable}
            className="w-full min-w-0"
            options={FILTER_COLUMNS.map((column) => ({ label: t(`filter.${column}.title`), value: column }))}
            value={getRuleColumn(rule)}
            onChange={(column) => dispatch({ type: "changeColumn", id: rule.id, column, context })}
        />
    );

    const buildOperatorColumn = (): ReactNode => {
        if (affiliateRule) {
            return (
                <Select
                    {...searchable}
                    className="w-full min-w-0"
                    value={affiliateRule.mode}
                    onChange={(mode) => update({ mode })}
                    options={[
                        { label: t("affiliate.in"), value: "in" },
                        { label: t("affiliate.not_in"), value: "not_in" },
                    ]}
                />
            );
        }
        if (weekdayRule) {
            return (
                <Select
                    {...searchable}
                    className="w-full min-w-0"
                    value={weekdayRule.mode}
                    onChange={(mode) => update({ mode })}
                    options={[
                        { label: t("weekday.in"), value: "in" },
                        { label: t("weekday.not_in"), value: "not_in" },
                    ]}
                />
            );
        }
        if (timeRule) {
            return (
                <Select
                    {...searchable}
                    className="w-full min-w-0"
                    options={operatorOptions(TIME_OPERATORS, timeRule.operator)}
                    value={timeRule.operator}
                    onChange={(operator) => update({ operator })}
                />
            );
        }
        if (capRule) {
            return (
                <Select
                    {...searchable}
                    className="w-full min-w-0"
                    options={operatorOptions(CAP_OPERATORS, capRule.operator)}
                    value={capRule.operator}
                    onChange={(operator) => update({ operator })}
                />
            );
        }
        const rule = fieldRule as FilterFieldRule;
        return (
            <Select
                {...searchable}
                className="w-full min-w-0"
                options={operatorOptions(FIELD_OPERATORS, rule.operator)}
                value={rule.operator}
                onChange={(operator) => update({ operator })}
            />
        );
    };

    const buildValueColumn = (): ReactNode => {
        if (affiliateRule) {
            return (
                <PartnerSelectSearchable
                    mode="multiple"
                    allowClear
                    status={status("affiliateIds")}
                    value={affiliateRule.affiliateIds}
                    onChange={(affiliateIds: string[]) => update({ affiliateIds })}
                    placeholder={t("affiliate.placeholder")}
                    className="w-full min-w-0"
                    maxTagCount="responsive"
                />
            );
        }

        if (weekdayRule) {
            return (
                <Select
                    {...searchable}
                    mode="multiple"
                    allowClear
                    className="w-full min-w-0"
                    status={status("days")}
                    value={weekdayRule.days}
                    onChange={(days: number[]) => update({ days: [...days].sort((a, b) => a - b) })}
                    placeholder={t("weekday.placeholder")}
                    maxTagCount="responsive"
                    options={WEEKDAYS.map((day) => ({ label: t(`filter.weekday.${WEEKDAY_KEYS[day]}`), value: day }))}
                />
            );
        }

        if (timeRule) {
            const timePicker = (field: "value" | "valueMax", placeholder: string) => (
                <TimePicker
                    className="w-full min-w-0"
                    format={TIME_FORMAT}
                    needConfirm={false}
                    showNow={false}
                    status={status(field)}
                    placeholder={placeholder}
                    value={toTimePickerValue(timeRule[field])}
                    onChange={(time) => update({ [field]: time ? time.format(TIME_FORMAT) : "" })}
                />
            );
            return RANGE_OPERATORS.includes(timeRule.operator) ? (
                <div style={{ display: "flex", gap: 8 }}>
                    {timePicker("value", t("time.from"))}
                    {timePicker("valueMax", t("time.to"))}
                </div>
            ) : (
                timePicker("value", t("time.value"))
            );
        }

        if (capRule) {
            return (
                <InputNumber
                    placeholder={t("cap.value")}
                    min={0}
                    status={status("value")}
                    value={capRule.value === "" ? null : Number(capRule.value)}
                    onChange={(value) => update({ value: value === null ? "" : String(value) })}
                    className="w-full"
                />
            );
        }

        const rule = fieldRule as FilterFieldRule;
        if (VALUE_LESS_OPERATORS.includes(rule.operator)) {
            return null;
        }
        if (RANGE_OPERATORS.includes(rule.operator)) {
            return (
                <div style={{ display: "flex", gap: 8 }}>
                    <Input
                        className="w-full min-w-0"
                        value={rule.value}
                        status={status("value")}
                        placeholder={t("field.value_min")}
                        onChange={(event) => update({ value: event.target.value })}
                    />
                    <Input
                        className="w-full min-w-0"
                        value={rule.valueMax}
                        status={status("valueMax")}
                        placeholder={t("field.value_max")}
                        onChange={(event) => update({ valueMax: event.target.value })}
                    />
                </div>
            );
        }
        return (
            <Input
                className="w-full min-w-0"
                value={rule.value}
                status={status("value")}
                placeholder={t("field.value")}
                onChange={(event) => update({ value: event.target.value })}
            />
        );
    };

    const buildFieldPickerColumn = (): ReactNode => {
        const rule = fieldRule as FilterFieldRule;
        return (
            <div className={hasError("tag") ? "rounded border border-red-500" : ""}>
                <TagTextInput
                    value={rule.tag ? `{${rule.tag}}` : ""}
                    onChange={handleTagChange}
                    categories={pickerCategories}
                    // "tag-only" pre-fills "{}" and immediately closes the very first typed char into
                    // a tag, so typing "{value}" ourselves breaks — "strict-single" is what
                    // modify.source in CC uses for the same "type a tag" UX: it auto-closes
                    // "{" -> "{}" and live-syncs into the picker while typing.
                    mode="strict-single"
                    maxTags={1}
                    disabled={!fieldsCategory}
                    placeholder={fieldsCategory ? t("field.placeholder") : t("field.no_structure")}
                />
            </div>
        );
    };

    const buildCapTypeColumn = (): ReactNode => {
        const rule = capRule as FilterCapRule;
        const { kind } = parseCapTag(rule.tag);
        return (
            <Select
                {...searchable}
                className="w-full min-w-0"
                status={status("tag")}
                placeholder={t("cap.type")}
                options={CAP_KIND_ORDER.map((value) => ({ label: t(`cap_type.${value}`), value }))}
                value={kind}
                onChange={(nextKind) => update({ tag: buildCapTag(parseCapTag(rule.tag).period, nextKind, rule.tag) })}
            />
        );
    };

    const buildCapPeriodColumn = (): ReactNode => {
        const rule = capRule as FilterCapRule;
        const { period } = parseCapTag(rule.tag);
        const periodOptions = getCapPeriodOptions(capTags, period).map((value) => ({
            label: t(`cap_period.${value}`),
            value,
        }));
        return (
            <Select
                {...searchable}
                className="w-full min-w-0"
                status={status("tag")}
                options={periodOptions}
                value={period}
                onChange={(nextPeriod) =>
                    update({ tag: buildCapTag(nextPeriod, parseCapTag(rule.tag).kind, rule.tag) })
                }
            />
        );
    };

    // Column layout per rule kind:
    // - affiliate, weekday, time: column | operator | value           (3, no picker/period)
    // - field:      column | field picker | operator | value           (4)
    // - period cap: column | [type] | operator | value | period        (4, or 5 for an offer — cap has a type)
    const columns: RowColumn[] =
        affiliateRule || timeRule || weekdayRule
            ? [
                  { span: 6, node: ruleColumnSelect },
                  { span: 6, node: buildOperatorColumn() },
                  { span: 12, node: buildValueColumn() },
              ]
            : fieldRule
              ? [
                    { span: 6, node: ruleColumnSelect },
                    { span: 6, node: buildFieldPickerColumn() },
                    { span: 6, node: buildOperatorColumn() },
                    { span: 6, node: buildValueColumn() },
                ]
              : capShowsKind
                ? [
                      { span: 5, node: ruleColumnSelect },
                      { span: 4, node: buildCapTypeColumn() },
                      { span: 5, node: buildOperatorColumn() },
                      { span: 5, node: buildValueColumn() },
                      { span: 5, node: buildCapPeriodColumn() },
                  ]
                : [
                      { span: 6, node: ruleColumnSelect },
                      { span: 6, node: buildOperatorColumn() },
                      { span: 6, node: buildValueColumn() },
                      { span: 6, node: buildCapPeriodColumn() },
                  ];

    return (
        <div>
            <Row gutter={[8, 8]} align="middle">
                {columns.map(({ span, node }, index) => {
                    const isLast = index === columns.length - 1;
                    return (
                        <Col xs={24} md={span} key={index}>
                            {isLast ? (
                                <div style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 0 }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>{node}</div>
                                    <Button
                                        type="text"
                                        icon={<DeleteOutlined />}
                                        onClick={() => dispatch({ type: "remove", id: rule.id })}
                                        aria-label="Remove filter"
                                    />
                                </div>
                            ) : (
                                node
                            )}
                        </Col>
                    );
                })}
            </Row>
            {errors.map((error) => (
                <Typography.Text key={`${error.field}-${error.messageKey}`} type="danger" className="block mt-1">
                    {t(error.messageKey, error.params)}
                </Typography.Text>
            ))}
        </div>
    );
};
